<template>
  <div v-if="!error" class="avatars-container">
    <div
      v-show="!isMobile()"
      class="avatar-list__item"
      v-for="user in firstFiveUsers"
      :key="user.id"
      data-test="avatar-list"
    >
      <UserAvatar
        :user="user"
        size="30px"
        :variant="avatarVariant(user)"
        :tooltip-text="getTooltipText(user)"
      />
    </div>
    <div v-if="users.length > MAX_AVATARS_IN_LIST || isMobile()">
      <AvatarDropdown
        :users="isMobile() ? usersSortedByCompletion : restOfUsers"
        :users-who-have-completed="usersWhoHaveCompleted"
        :trigger-text="dropdownTriggerText()"
        :action-type="props.actionType"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import axios from "axios";
import useWindowWidth from "../../composables/useWindowWidth";
import { UserProfileApiModel, NotificationApiModel } from "../../api-models";
import UserAvatar from "../reusables/user-avatar.vue";
import useTeamMembers from "../../composables/useTeamMembers";
import useGetUserPathwayData from "../../composables/useGetUserPathwayData";
import AvatarDropdown from "./avatar-dropdown.vue";

const props = defineProps<{
  teamId: string;
  resourceId?: string;
  actionType: "Pathway" | "Resource" | "Invite";
}>();

const users = ref<UserProfileApiModel[]>([]);
const usersWhoHaveCompleted = ref<UserProfileApiModel[]>([]);

const MAX_AVATARS_IN_LIST = 4;

const { isMobile } = useWindowWidth();

const { teamMembers, error, fetchUserProfiles } = useTeamMembers(props.teamId);
const { getPersonalPathwayStepCompletionById, getTeamPathwayStepCompletionById } =
  useGetUserPathwayData();

async function fetchUserNotifications(userId: string) {
  const response = await axios.get<NotificationApiModel[]>(`/api/usernotifications/${userId}`);
  const notifications = response.data;
  return notifications;
}

async function usersWhoHaveCompletedTheResource(
  userProfiles: UserProfileApiModel[],
  resourceId?: string,
) {
  if (!resourceId) return [];

  if (props.actionType === "Pathway") {
    const pathwayUsersPromises = userProfiles.map(async (user) => {
      const hasCompletedPersonal = await getPersonalPathwayStepCompletionById(user.id, resourceId);
      const hasCompletedTeam = await getTeamPathwayStepCompletionById(user.id, resourceId);
      return hasCompletedPersonal || hasCompletedTeam ? user : null;
    });

    const pathwayUsersWithNulls = await Promise.all(pathwayUsersPromises);
    const pathwayUsers = pathwayUsersWithNulls.filter((user) => user !== null);

    return pathwayUsers as UserProfileApiModel[];
  }

  return userProfiles.filter(async (user) => {
    const notifications = await fetchUserNotifications(user.id);
    return notifications.some((notification) => {
      return notification.resourceId === resourceId && notification.actionStatus === "Completed";
    });
  });
}

watch(teamMembers, async (newTeamMembers, oldTeamMembers) => {
  if (newTeamMembers === undefined) return;
  users.value = await fetchUserProfiles(newTeamMembers.currentMembers);
  usersWhoHaveCompleted.value = await usersWhoHaveCompletedTheResource(
    users.value,
    props.resourceId,
  );
});

const usersSortedByCompletion = computed(() => {
  const copy = [...users.value];
  return copy.sort((a, b) => {
    const aHasCompleted = usersWhoHaveCompleted.value.includes(a);
    const bHasCompleted = usersWhoHaveCompleted.value.includes(b);

    if (aHasCompleted && !bHasCompleted) return -1;
    if (!aHasCompleted && bHasCompleted) return 1;
    return 0;
  });
});

const firstFiveUsers = computed(() => {
  return usersSortedByCompletion.value.slice(0, MAX_AVATARS_IN_LIST);
});

const restOfUsers = computed(() => {
  return usersSortedByCompletion.value.slice(MAX_AVATARS_IN_LIST);
});

function avatarVariant(user: UserProfileApiModel) {
  if (props.actionType === "Invite") return "default";
  return usersWhoHaveCompleted.value.includes(user) ? "default" : "faded";
}

function getTooltipText(user: UserProfileApiModel) {
  if (props.actionType === "Invite") return "";
  return usersWhoHaveCompleted.value.includes(user)
    ? `${user.firstName} ${user.lastName} (completed)`
    : `${user.firstName} ${user.lastName} (incomplete)`;
}

function dropdownTriggerText(): string {
  if (isMobile()) return "Team tracker";
  return `+${users.value.length - MAX_AVATARS_IN_LIST}`;
}
</script>

<style scoped lang="less">
@padding: 5px;

.avatars-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  gap: @padding;
  padding: @padding;
  background: #ffffff;
  border-radius: 100vmax;
  max-width: 50%;

  @media (max-width: 1064px) {
    max-width: 100%;
  }
}
</style>
